/**
 * useJobExpenseLedger — Phase D
 *
 * Role-gated read access to the in-memory expense ledger for a single job.
 * Mirrors the DB SELECT policy from `db/migrations/0003_expense_ledger.sql`:
 * unauthorized roles always see an empty list.
 *
 * Notably EXCLUDED (returns []): installer, subcontractor, crew_lead,
 * helper, sales_rep, sales_manager, marketing roles, and every
 * contractor/installer-portal role.
 */
import { useMemo } from "react";
import { useRole } from "@/data/RoleContext";
import { useExpenseLedgerContext } from "@/data/jobCost/ExpenseLedgerContext";
import {
  canReadExpenseLedger,
} from "@/data/jobCost/expenseLedgerReader";
import type { ExpenseLedgerEntry } from "@/data/jobCost/expenseLedgerBridge";

export interface UseJobExpenseLedgerResult {
  /** Entries scoped to the job. Empty when role is unauthorized. */
  entries: ExpenseLedgerEntry[];
  /** True when the current role is allowed to read the ledger. */
  authorized: boolean;
}

export function useJobExpenseLedger(
  jobId: string | null | undefined,
): UseJobExpenseLedgerResult {
  const { role } = useRole();
  const { getEntriesByJob } = useExpenseLedgerContext();

  return useMemo<UseJobExpenseLedgerResult>(() => {
    const authorized = canReadExpenseLedger(role);
    if (!authorized || !jobId) return { entries: [], authorized };
    return { entries: getEntriesByJob(jobId), authorized };
  }, [role, jobId, getEntriesByJob]);
}
